import { FC, FormEvent, ReactNode, useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { isAdminMfaRequired } from "config/amplifyAuth";
import { useAuthUser } from "hooks/useAuthUser";
import { useAppDispatch } from "hooks/useAppDispatch";
import { ROLES } from "types/user";
import {
  beginTotpSetupThunk,
  fetchMfaPreferenceThunk,
  logoutThunk,
  verifyTotpSetupThunk,
} from "reduxStore/auth/thunks";

type Props = {
  children: ReactNode;
};

/**
 * When REACT_APP_REQUIRE_ADMIN_MFA is enabled, blocks admin profiles that have
 * not enrolled Cognito TOTP. SAML-only users that cannot enroll fall through
 * with a warning after a failed preference read so they are not permanently locked.
 */
export const AdminMfaGate: FC<Props> = ({ children }) => {
  const { user } = useAuthUser();
  const dispatch = useAppDispatch();
  const required = isAdminMfaRequired();
  const isAdmin = user?.role === ROLES.ADMIN;

  const [checking, setChecking] = useState(required && isAdmin);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [secret, setSecret] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!required || !isAdmin) {
      setChecking(false);
      setNeedsSetup(false);
      return;
    }

    let cancelled = false;
    void (async () => {
      setChecking(true);
      try {
        const preference = await dispatch(fetchMfaPreferenceThunk()).unwrap();
        if (cancelled) return;
        setNeedsSetup(!preference.enabled);
      } catch {
        // Federated users may not support software MFA — do not hard-lock.
        if (cancelled) return;
        setNeedsSetup(false);
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dispatch, isAdmin, required]);

  const startSetup = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const details = await dispatch(beginTotpSetupThunk()).unwrap();
      setSecret(details.sharedSecret);
    } catch (err) {
      setError(
        typeof err === "string" ? err : "Could not start MFA setup.",
      );
    } finally {
      setSaving(false);
    }
  }, [dispatch]);

  const onVerify = async (event: FormEvent) => {
    event.preventDefault();
    if (!code.trim()) {
      setError("Enter the authenticator code.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await dispatch(
        verifyTotpSetupThunk({ code, completeSignIn: false }),
      ).unwrap();
      setNeedsSetup(false);
    } catch (err) {
      setError(typeof err === "string" ? err : "Code was not accepted.");
    } finally {
      setSaving(false);
    }
  };

  if (!required || !isAdmin) {
    return <>{children}</>;
  }

  if (checking) {
    return (
      <Box
        minHeight="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!needsSetup) {
    return <>{children}</>;
  }

  return (
    <Box
      minHeight="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      px={2}
    >
      <Box maxWidth={480} width="100%">
        <Typography variant="h5" gutterBottom>
          Admin MFA required
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Privileged VetNebula admin accounts must enroll an authenticator app
          before accessing education records.
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          Cognito user-pool MFA should also be set to optional/required for the
          admin group so challenges apply at sign-in.
        </Alert>
        {!secret ? (
          <Button
            variant="contained"
            disabled={saving}
            onClick={() => void startSetup()}
          >
            Set up authenticator
          </Button>
        ) : (
          <Stack component="form" spacing={2} onSubmit={onVerify}>
            <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
              {secret}
            </Typography>
            <TextField
              label="Authenticator code"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              autoComplete="one-time-code"
              required
            />
            <Button type="submit" variant="contained" disabled={saving}>
              Verify and continue
            </Button>
          </Stack>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        <Button
          sx={{ mt: 2 }}
          onClick={() => {
            void dispatch(logoutThunk()).then(() => {
              window.location.assign("/");
            });
          }}
        >
          Sign out
        </Button>
      </Box>
    </Box>
  );
};
